import { Link } from "react-router-dom";
import { Golf, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { suggestClub, adjustDistanceForWind, CLUB_CATALOG } from "../lib/clubs";

/**
 * Compact in-round chip that suggests a club for the current shot.
 * - Takes raw `distance` (yds to pin), `wind` ({mph, relativeDeg}), the
 *   user's profile `clubYardages` AND a `dispersion` map of real shot stats.
 * - When ≥5 shots are logged for a club, uses the dispersion p50 as the
 *   "real" yardage and surfaces a confidence range (p25–p75).
 */
export default function ClubSuggestionChip({
  distance,
  wind,
  elevationAdj,
  clubYardages,
  dispersion,
  onGreen,
}) {
  if (onGreen || distance == null || distance === "—") return null;

  // Merge profile yardages with dispersion p50 (preferring real data).
  // Only entries from the canonical CLUB_CATALOG are kept.
  const effectiveYards = {};
  CLUB_CATALOG.forEach((c) => {
    const disp = dispersion?.[c.key];
    if (disp && disp.count >= 5) {
      effectiveYards[c.key] = Math.round(disp.p50);
    } else if (clubYardages?.[c.key] != null) {
      effectiveYards[c.key] = clubYardages[c.key];
    }
  });

  // Decide what to render.
  // 1) No bag yet → "Set your yardages" CTA (existing onboarding).
  // 2) Bag exists → branded Smart Caddie card with club pick + wind + Bag IQ progress.
  const totalShots = dispersion
    ? Object.values(dispersion).reduce((n, d) => n + (d?.count || 0), 0)
    : 0;

  const hasBag = Object.keys(effectiveYards).length > 0;
  if (!hasBag) {
    return (
      <Link
        to="/app/profile/clubs"
        data-testid="club-bag-cta"
        className="mt-3 flex items-center gap-2 rounded-2xl px-3 py-2 bg-white/10 hover:bg-white/15 transition-colors text-white"
      >
        <Golf size={18} weight="fill" />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider opacity-80 font-bold">Smart Caddie</div>
          <div className="text-[11px] opacity-75 leading-tight">Set your club yardages to unlock smart picks ›</div>
        </div>
        <ArrowRight size={14} weight="bold" />
      </Link>
    );
  }

  const windMph = wind?.mph ?? null;
  const windRel = wind?.relativeDeg ?? null;
  const windAdjusted = adjustDistanceForWind(distance, windMph, windRel) ?? distance;
  // Fold tee→pin elevation into the play distance too — same tour rule of
  // thumb the watch uses (~1.1 yd per 1m of rise/fall). Headwind+uphill
  // stack additively so the recommended club is always the one for the
  // TRUE play distance, not the GPS pin distance.
  const elevDelta = elevationAdj?.delta_yds ?? 0;
  const playYds = Math.round(windAdjusted + elevDelta);
  const result = suggestClub(effectiveYards, playYds);
  if (!result) return null;
  const { club, delta } = result;

  // Pull dispersion for the chosen club (if any) — drives the range chip.
  const disp = dispersion?.[club.key];
  const hasRealData = disp && disp.count >= 5;

  let advice;
  if (Math.abs(delta) <= 3) advice = "Stock shot";
  else if (delta < 0) advice = `Easy swing · -${Math.abs(delta)} yds`;
  else advice = `Hit it hard · +${delta} yds`;

  // Wind + elevation chips — both colour-coded so headwind/uphill (harder
  // shot, club up) reads red, tailwind/downhill (easier, club down) reads
  // green. Mirrors the watch face vocabulary exactly.
  const windDelta = Math.round(windAdjusted - distance);
  const isHeadwind = windDelta > 2;
  const isTailwind = windDelta < -2;
  const windChipClass = isHeadwind
    ? "bg-red-500/20 text-red-200 border-red-400/40"
    : isTailwind
      ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40"
      : "bg-white/10 text-white/70 border-white/20";
  const isUphill = elevDelta > 2;
  const isDownhill = elevDelta < -2;
  const elevChipClass = isUphill
    ? "bg-red-500/20 text-red-200 border-red-400/40"
    : isDownhill
      ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40"
      : "bg-white/10 text-white/70 border-white/20";

  return (
    <div
      data-testid="club-suggestion-chip"
      className="mt-3 rounded-2xl px-3 py-2.5 bg-[var(--accent-turf)] text-[var(--primary)] shadow-sm"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-75">
          <Sparkle size={11} weight="fill" /> Smart Caddie
        </div>
        {/* Bag IQ status badge — at a glance "are these personal numbers
            yet?" — encourages users to keep logging shots. */}
        <span
          className="text-[9px] font-bold uppercase tracking-wider opacity-70"
          data-testid="club-suggestion-bagiq"
          title={hasRealData
            ? `Personalised from ${disp.count} of your real shots`
            : "Using bag-average yardages. Log more shots to personalise."}
        >
          {hasRealData ? `Personal · ${disp.count} shots` : `Bag IQ ${totalShots}/5`}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <Golf size={20} weight="fill" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className="font-heading font-black text-lg leading-none"
              data-testid="club-suggestion-club"
            >
              {club.label}
            </span>
            {hasRealData ? (
              <span
                className="font-mono-num text-[11px] opacity-90 leading-none"
                data-testid="club-suggestion-range"
              >
                {Math.round(disp.p50)} yds · range {Math.round(disp.p25)}–{Math.round(disp.p75)}
              </span>
            ) : (
              <span className="font-mono-num text-[11px] opacity-70 leading-none">
                ({club.yards} yds avg)
              </span>
            )}
            {(isHeadwind || isTailwind) && (
              <span
                data-testid="club-suggestion-wind-chip"
                className={`font-mono-num text-[11px] font-bold px-2 py-0.5 rounded-full border ${windChipClass}`}
                title={`${windMph}mph ${isHeadwind ? "headwind" : "tailwind"} adjusts ${windDelta > 0 ? "+" : ""}${windDelta}yds`}
              >
                {isHeadwind ? "↑" : "↓"} wind {windDelta > 0 ? "+" : ""}{windDelta}
              </span>
            )}
            {(isUphill || isDownhill) && (
              <span
                data-testid="club-suggestion-elev-chip"
                className={`font-mono-num text-[11px] font-bold px-2 py-0.5 rounded-full border ${elevChipClass}`}
                title={`${elevationAdj?.delta_m}m ${isUphill ? "uphill" : "downhill"} adjusts ${elevDelta > 0 ? "+" : ""}${elevDelta}yds`}
              >
                {isUphill ? "↑" : "↓"} hill {elevDelta > 0 ? "+" : ""}{elevDelta}
              </span>
            )}
            {(isHeadwind || isTailwind || isUphill || isDownhill) && (
              <span
                data-testid="club-suggestion-play-as"
                className="font-mono-num text-[11px] font-bold px-2 py-0.5 rounded-full border bg-[var(--primary)] text-white border-[var(--primary)]"
                title={`Wind + hill — play it as ${playYds} yds`}
              >
                play {playYds}
              </span>
            )}
          </div>
          <div
            className="text-[11px] opacity-85 leading-tight mt-0.5"
            data-testid="club-suggestion-advice"
          >
            {advice}
          </div>
        </div>
      </div>
    </div>
  );
}
